import { Type } from '@angular/core';
import { ActiveMembersWidgetComponent } from './components/active-members-widget/active-members-widget.component';
import { AverageTicketWidgetComponent } from './components/average-ticket-widget/average-ticket-widget.component';
import { VisitsPerMemberWidgetComponent } from './components/visits-per-member-widget/visits-per-member-widget.component';
import { LiftBreakdownWidgetComponent } from './components/lift-breakdown-widget/lift-breakdown-widget.component';
import { RewardsThisPeriodWidgetComponent } from './components/rewards-this-period-widget/rewards-this-period-widget.component';
import { CustomersByHabitWidgetComponent } from './components/customers-by-habit-widget/customers-by-habit-widget.component';
import { SalesComparisonWidgetComponent } from './components/sales-comparison-widget/sales-comparison-widget.component';
import { CustomersWhoCameBackWidgetComponent } from './components/customers-who-came-back-widget/customers-who-came-back-widget.component';
import { HowYouCompareWidgetComponent } from './components/how-you-compare-widget/how-you-compare-widget.component';
import { PeerVerticalNameWidgetComponent } from './components/peer-vertical-name-widget/peer-vertical-name-widget.component';
import { ProgramImpactWidgetComponent } from './components/program-impact-widget/program-impact-widget.component';
import { NextStepsWidgetComponent } from './components/next-steps-widget/next-steps-widget.component';
import { WidgetSize } from './models/loyalty-dashboard.model';

/**
 * Add a widget (step 2 of 3): register `type: Component` here. The key must match the widget
 * `type` the backend streams (its key in `LoyaltyDashboardWidgets` / each NDJSON envelope).
 */
export const WIDGET_REGISTRY: Record<string, Type<unknown>> = {
  programImpact: ProgramImpactWidgetComponent,
  activeMembers: ActiveMembersWidgetComponent,
  visitsPerMember: VisitsPerMemberWidgetComponent,
  liftBreakdown: LiftBreakdownWidgetComponent,
  rewardsThisPeriod: RewardsThisPeriodWidgetComponent,
  averageTicket: AverageTicketWidgetComponent,
  customersByHabit: CustomersByHabitWidgetComponent,
  salesComparison: SalesComparisonWidgetComponent,
  customersWhoCameBack: CustomersWhoCameBackWidgetComponent,
  howYouCompare: HowYouCompareWidgetComponent,
  peerVerticalName: PeerVerticalNameWidgetComponent,
  nextSteps: NextStepsWidgetComponent,
};

interface WidgetPlacement {
  type: string;
  size: WidgetSize;
  order: number;
  visible: boolean;
  placeholder?: boolean;
  dataOnly?: boolean;
  page?: 1 | 2;
}

/**
 * Add a widget (step 3 of 3): add a placement here. `size` is the widget's size class (see
 * `WidgetSize`), which maps to a column span per breakpoint in the SCSS. Set `visible: false` to
 * keep a widget registered but hidden. Reordering/resizing the dashboard is done entirely here.
 */
export const WIDGET_LAYOUT: WidgetPlacement[] = [
  { type: 'peerVerticalName', size: 'stat', order: 1, visible: true, dataOnly: true },
  { type: 'programImpact', size: 'hero', order: 5, visible: true, placeholder: true, page: 1 },
  { type: 'liftBreakdown', size: 'statWide', order: 6, visible: true, page: 1 },
  { type: 'activeMembers', size: 'stat', order: 10, visible: true, page: 1 },
  { type: 'visitsPerMember', size: 'stat', order: 20, visible: true, page: 1 },
  { type: 'averageTicket', size: 'stat', order: 30, visible: true, page: 1 },
  { type: 'customersWhoCameBack', size: 'stat', order: 40, visible: true, page: 1 },
  { type: 'salesComparison', size: 'wide', order: 50, visible: true, page: 1 },
  { type: 'rewardsThisPeriod', size: 'half', order: 70, visible: true, page: 2 },
  { type: 'customersByHabit', size: 'half', order: 80, visible: true, page: 2 },
  { type: 'howYouCompare', size: 'half', order: 90, visible: true, page: 2 },
  { type: 'nextSteps', size: 'guide', order: 110, visible: true, placeholder: true, page: 2 },
];

export interface ResolvedWidget {
  type: string;
  component: Type<unknown>;
  size: WidgetSize;
  placeholder: boolean;
  dataOnly: boolean;
  page: 1 | 2;
}

export function resolveLayout(): ResolvedWidget[] {
  return WIDGET_LAYOUT.filter((placement) => {
    if (!placement.visible) {
      return false;
    }
    if (!WIDGET_REGISTRY[placement.type]) {
      console.warn(`[loyalty-dashboard] No component registered for widget type "${placement.type}"; skipping.`);
      return false;
    }
    return true;
  })
    .sort((a, b) => a.order - b.order)
    .map((placement) => ({
      type: placement.type,
      component: WIDGET_REGISTRY[placement.type],
      size: placement.size,
      placeholder: placement.placeholder ?? false,
      dataOnly: placement.dataOnly ?? false,
      page: placement.page ?? 1,
    }));
}
